// Pure helpers for the partner pages feature (public profile, curated shelf, performance).
// Kept free of React so they can be unit-tested; see partnerPages.test.js.

export const PARTNER_ROOT_DOMAIN = process.env.REACT_APP_PARTNER_ROOT_DOMAIN || "wasaa.markets";
export const DESCRIPTION_MAX = 600;
export const CURATED_MAX = 12;

// Same rule as the API (docs/partner-pages.md): 1-63 chars, lowercase letters, digits and
// inner hyphens. Reserved names are refused because they are also refused as subdomains.
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
export const RESERVED_SLUGS = new Set(
  "www admin dashboard api app apps mail email static cdn assets img images status docs help support blog news partners partner partnerships embed auth login signup account wallet markets market activity developers leads revenue settings surfaces traders".split(" "),
);

/** Turns what someone typed into slug characters as they type (spaces become hyphens). */
export function normalizeSlugInput(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 63);
}

/** Suggests a slug from a partner's name. */
export function slugFromName(name) {
  return normalizeSlugInput(String(name ?? "").trim()).replace(/^-+|-+$/g, "");
}

/** Null when the slug is usable. Every partner has a site address, so an empty one is a problem. */
export function slugProblem(slug) {
  const s = String(slug ?? "");
  if (s === "") return "Choose a site address.";
  if (s.length > 63) return "Use at most 63 characters.";
  if (!SLUG_PATTERN.test(s)) return "Use lowercase letters, numbers and hyphens, starting and ending with a letter or number.";
  if (RESERVED_SLUGS.has(s)) return `"${s}" is reserved. Choose another address.`;
  return null;
}

export function partnerPageUrls(slug, root = PARTNER_ROOT_DOMAIN) {
  if (!slug) return null;
  return { page: `https://${root}/partners/${slug}`, subdomain: `https://${slug}.${root}` };
}

export function isHexColour(value) {
  return /^#[0-9a-fA-F]{6}$/.test(String(value ?? ""));
}

const PROFILE_FIELDS = ["slug", "description", "brandPrimary", "brandAccent"];

/** The profile form as editable strings ("" for a field that is not set). */
export function profileForm(partner) {
  return {
    slug: partner?.slug || "",
    description: partner?.description || "",
    brandPrimary: partner?.brandPrimary || "",
    brandAccent: partner?.brandAccent || "",
  };
}

/**
 * The PATCH body for /portal/partners/:id/profile: only the fields that changed, with a
 * cleared field sent as null. Colours are lowercased; the description is trimmed.
 */
export function profilePatch(partner, form) {
  const before = profileForm(partner);
  const patch = {};
  for (const key of PROFILE_FIELDS) {
    const colour = key === "brandPrimary" || key === "brandAccent";
    let next = String(form[key] ?? "").trim();
    if (colour) next = next.toLowerCase();
    const prev = colour ? before[key].toLowerCase() : before[key];
    if (next !== prev) patch[key] = next === "" ? null : next;
  }
  return patch;
}

/** Returns a field -> message map for the profile form; empty when it is valid. */
export function profileProblems(form) {
  const problems = {};
  const slug = slugProblem(String(form.slug ?? "").trim());
  if (slug) problems.slug = slug;
  if (String(form.description ?? "").trim().length > DESCRIPTION_MAX) problems.description = `Keep it to ${DESCRIPTION_MAX} characters.`;
  for (const key of ["brandPrimary", "brandAccent"]) {
    const v = String(form[key] ?? "").trim();
    if (v && !isHexColour(v)) problems[key] = "Use a colour like #1a2b3c.";
  }
  return problems;
}

/** Maps a profile API error to the field it belongs to. */
export function profileErrorField(code) {
  if (code === "INVALID_PARTNER_SLUG" || code === "PARTNER_SLUG_TAKEN") return "slug";
  if (code === "INVALID_BRAND_COLOUR") return "brand";
  return null;
}

/** Moves the item at `index` by `delta` places; out-of-range moves return the same list. */
export function moveItem(list, index, delta) {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/** Appends a market to the curated list unless it is already there or the list is full. */
export function addCurated(list, market, max = CURATED_MAX) {
  if (!market || list.length >= max || list.some((m) => m.marketId === market.marketId)) return list;
  return [...list, market];
}

export function removeCurated(list, marketId) {
  return list.filter((m) => m.marketId !== marketId);
}

export function sameOrder(a, b) {
  return a.length === b.length && a.every((m, i) => m.marketId === b[i].marketId);
}

export const SOURCE_ORDER = ["PARTNER", "MAIN_SITE", "OTHER_PARTNER"];

/**
 * Performance `bySource` rows in a fixed order, with zero rows for any missing source and
 * each source's share (0-100) of the total by the given measure.
 */
export function sourceBreakdown(bySource, measure = "trades") {
  const rows = SOURCE_ORDER.map((source) => {
    const found = (bySource || []).find((r) => r.source === source);
    return { source, trades: found?.trades ?? 0, volume: found?.volume ?? "0", uniqueTraders: found?.uniqueTraders ?? 0 };
  });
  const total = rows.reduce((sum, r) => sum + Number(r[measure] || 0), 0);
  return rows.map((r) => ({ ...r, percent: total > 0 ? (Number(r[measure] || 0) / total) * 100 : 0 }));
}

/** Adds decimal-string amounts, to 4 places as the API sends them. */
export function sumAmounts(values) {
  const total = values.reduce((sum, v) => sum + Math.round(Number(v || 0) * 10000), 0);
  return (total / 10000).toFixed(4);
}

/** Validates a share percentage the way the API does: 0-100 with at most 2 decimals. */
export function validSharePercent(value) {
  return /^\d{1,3}(\.\d{1,2})?$/.test(String(value ?? "").trim()) && Number(value) >= 0 && Number(value) <= 100;
}

/** Ready-made colour pairs for a partner's site: a primary for buttons and links, an accent for highlights. */
export const SITE_PALETTES = [
  { id: "ocean", name: "Ocean", primary: "#0b5fff", accent: "#00c2a8" },
  { id: "emerald", name: "Emerald", primary: "#0a7f3f", accent: "#f2b705" },
  { id: "sunset", name: "Sunset", primary: "#e4572e", accent: "#ffc914" },
  { id: "royal", name: "Royal", primary: "#5b21b6", accent: "#f472b6" },
  { id: "crimson", name: "Crimson", primary: "#c1121f", accent: "#f4a261" },
  { id: "midnight", name: "Midnight", primary: "#1f2937", accent: "#38bdf8" },
  { id: "savanna", name: "Savanna", primary: "#b45309", accent: "#16a34a" },
  { id: "teal", name: "Teal", primary: "#0f766e", accent: "#f97316" },
];

/** The preset a colour pair matches, if any. */
export function paletteFor(primary, accent) {
  const p = String(primary ?? "").toLowerCase();
  const a = String(accent ?? "").toLowerCase();
  return SITE_PALETTES.find((palette) => palette.primary === p && palette.accent === a) ?? null;
}

/** Black or white text, whichever reads better on the colour. */
export function readableOn(hex) {
  if (!isHexColour(hex)) return "#ffffff";
  const n = Number.parseInt(hex.slice(1), 16);
  const channel = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
  return luminance > 0.4 ? "#0b1220" : "#ffffff";
}
